import {DatePipe} from '@angular/common';
import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {NgxSpinnerService} from 'ngx-spinner';
import {PatientScreenTypeEnum} from '../../../enums/patient-screen-enum';
import {IsPermissionExist} from '../../../helpers/get-permissions';
import {PspEvaPermission, PspLillyPermission} from '../../../_constants/permissions.constant';
import {FilterParams, IPspSubscriptionDTO} from '../../../_models/patient.model';
import {CityService} from '../../../_services/city.service';
import {PatientService} from '../../../_services/patient.service';
import {TableColumn} from '@swimlane/ngx-datatable';
import {ISmartTableMessage} from '../../../_interfaces/smart-table/i-message';
import {ISmartTablePagination} from '../../../_interfaces/smart-table/i-pagination';
import {PspSubscriptionStatusEnum} from '../../../enums/psp-subscription-status-enum';
import {PspSubscriptionService} from '../../../_services/PSP/psp-subscription.service';
import {ExcelService} from '../../../_services/excel.service';
import {PspCompanyEnum} from '../../../enums/psp-company.enum';
import { PreviewDocumentsModalComponent } from '@components/preview-documents-modal/preview-documents-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MedicationRequestService } from 'src/app/_services/PSP/medication-request.service';
import { showNotification } from 'src/app/helpers/show-toast';
import { ToastrService } from 'ngx-toastr';
import { isArabic as isArabicHelper } from 'src/app/helpers/IsArabic';

@Component({
  selector: 'app-psp-patients',
  templateUrl: './psp-patients.component.html',
  styleUrls: ['./psp-patients.component.scss']
})
export class PspPatientsComponent implements OnInit {
  searchParams: FilterParams = {
    currentPage: 1,
    pageSize: 10,
    companyId: 0
  };
  patientList: IPspSubscriptionDTO[];
  totalPages = 2;
  cityList = [];
  columns: TableColumn[];
  totalItems: number;
  isArabic = isArabicHelper;

  @ViewChild('nationalIdPassportIdRef') public nationalIdPassportIdRef: TemplateRef<any>;
  @ViewChild('previewDocumentRef') public previewDocumentRef: TemplateRef<any>;
  @ViewChild('subscriptionHistoryTemplate', { static: true }) subscriptionHistoryTemplate: TemplateRef<any>;

  messages: ISmartTableMessage = {
    emptyMessage: 'Hmm, There are no Patients for this search criteria 🥺',
    totalMessage: 'total',
    selectedMessage: ''
  };
  searchValue = '';

  constructor(
    private patientService: PatientService,
    private spinner: NgxSpinnerService,
    private cityService: CityService,
    private router: Router,
    private datePipe: DatePipe,
    private pspSubscriptionService: PspSubscriptionService,
    private excelService: ExcelService,
    private modalService: NgbModal,
    private medicalRequestService: MedicationRequestService,
    private toastr: ToastrService,


  ) {}

  ngOnInit(): void {
    this.initializePatientsSubscriptions();
    this.patientService.setPatientScreenType(PatientScreenTypeEnum.PspPatients);
  }

  private initializePatientsSubscriptions(): void {
    this.spinner.show();
    this.patientList = [];

    this.pspSubscriptionService.getAllPaginated(this.searchParams).subscribe(response => {
      this.cityService.getAllCities().subscribe((res) => {
        this.cityList = res;
        this.patientList = response.data.items.map((subscription) => {
          const lastMedicationRequest = subscription.pspMedicationRequests?.pop();

          subscription.patient.cityName = this.cityList.find((city) => city.id === subscription.patient.cityId)?.enName;
          subscription.flagged = subscription.status.id === PspSubscriptionStatusEnum.Suspended ? 'Yes' : 'No';
          subscription.lastDeliveredOrderDate = lastMedicationRequest?.dischargeDate ?? null;
          subscription.program.company.pspCode = subscription.patient.pspPatients.filter(
            (p: any) => p.companyId === subscription.program.company.id
          )[0]?.pspCode;
          subscription.rocheSubscriptionCode = subscription.program.company.id === PspCompanyEnum.Roche
              ? `${subscription.doctorCode}-${subscription.program.company.pspCode}`
              : null;

          return subscription;
        });

        this.initializePatientsColumns();

        this.totalItems = response.data.pagination.totalItems;
        this.totalPages = response.data.pagination.totalPages;
        this.searchParams.currentPage = response.data.pagination.currentPage;

        this.spinner.hide();
      });
    }, error => console.error(error));
  }

  initializePatientsColumns(): void {
    const columns: any = [
      {
        prop: 'patient.fullName',
        name: 'Patient Name'
      },
      {
        prop: 'patient.phone',
        name: 'Mobile Number'
      },
      {
        prop: 'nationalIdpassportId',
        name: 'National Id / Passport Id',
        cellTemplate: this.nationalIdPassportIdRef,
        width: 220
      },
      {
        prop: 'patient.cityName',
        name: 'City'
      },
      {
        prop: 'program.company.pspCode',
        name: 'Psp Code'
      },
      {
        prop: 'program.company.name',
        name: 'Company'
      },
      {
        prop: 'program.name',
        name: 'Subscribed Program',
        width: 220
      },
    ];

    if (this.searchParams.companyId === PspCompanyEnum.Eva || this.searchParams.companyId === 0) {
      columns.push(...[
        {
          prop: 'insulinTypeName',
          name: 'Insulin Type',
        },
        {
          prop: 'pspCenterName',
          name: 'Referral Center',
        },
      ]);
    }

    if (this.searchParams.companyId === PspCompanyEnum.Roche || this.searchParams.companyId === 0) {
      columns.push(...[
        {
          prop: 'doctorCode',
          name: 'Doctor Code',
          width: 220
        },
        {
          prop: 'rocheSubscriptionCode',
          name: 'Roche Subscription Code',
          width: 220
        },
        {
          prop: 'subscriptionHistory',
          name: 'Subscription History',
          width: 190,
          cellTemplate: this.subscriptionHistoryTemplate,
        }
      ]);
    }

    columns.push(...[
      {
        prop: 'status.status',
        name: 'Subscribed Program Status',
        width: 260
      },
      {
        prop: 'creationDate',
        name: 'Subscription Date',
        pipe: {
          transform: (val) => this.datePipe.transform(val, 'MMM d, y, h:mm a')
        },
        width: 220
      },
      {
        prop: 'pspType.name',
        name: 'Patient Type'
      },
      {
        prop: 'lastDeliveredOrderDate',
        name: 'last Delivered Order Date',
        pipe: {
          transform: (val) => this.datePipe.transform(val, 'MMM d, y, h:mm a')
        },
        width: 245
      },
      {
        prop: 'flagged',
        name: 'Flagged'
      },
      {
        name: 'Documents',
        cellTemplate: this.previewDocumentRef,
      }
    ]);

    this.columns = columns;
  }

  onChangePage(pageDetails: ISmartTablePagination): void{
    this.searchParams.currentPage = pageDetails.offset + 1;

    this.initializePatientsSubscriptions();
  }

  resetSearchParams(): void {
    this.searchValue = '';

    Object.keys(this.searchParams).forEach((key) => {
      if (key === 'searchTerm') {
        delete this.searchParams[key];
      }
    });
  }

  getAllCities(): void {
    this.cityService.getAllCities().subscribe((res) => {
      this.cityList = res;
    });
  }

  onSearchPatients(searchValue: string): void {
    this.searchValue = searchValue;
    this.searchParams.currentPage = 1;

    if (searchValue.length > 0) {
      this.searchParams.searchTerm = searchValue;
    } else {
      this.resetSearchParams();
    }

    this.initializePatientsSubscriptions();
  }

  routeToPatientDetails(patient: any): void{
    this.router.navigate(['/patient-details/', patient.selected[0].patient.patientId]);
  }

  exportToExcel(): void{
    const params = JSON.parse(JSON.stringify(this.searchParams));
    delete params.currentPage;
    delete params.pageSize;

    this.pspSubscriptionService
      .getAllReport(params)
      .subscribe(res => {
        const data = (this.searchParams.companyId !== 0 && this.searchParams.companyId !== PspCompanyEnum.Roche)
          ? res.data.map(item => {
            delete item.doctorCode;
            delete item.rocheSubscriptionCode;
            delete item.subscriptionHistory;
            return item;
          })
          : res.data;

        for (let i = 0; i < data?.length; i++) {
          for (let j = 0; j < data[i].files?.length; j++) {
            const fileHeaderName = `file_${j + 1}`;
            data[i][fileHeaderName] = `https://limitlesscarestorage.blob.core.windows.net/${data[i].files[j]}`;
          }

          delete data[i].files;

          if (this.searchParams.companyId === PspCompanyEnum.Roche || this.searchParams.companyId === PspCompanyEnum.Lilly) {
            delete data[i].insulinType;
            delete data[i].pspCenter;
          }
        }

        this.excelService.download(data, `Psp`);
      });
  }

  onCompanySelected(companyId: number): void {
    this.searchParams.companyId = companyId;
    this.searchParams.currentPage = 1;

    this.initializePatientsSubscriptions();
  }

  hasDocuments(patientData: any):boolean{
    return patientData.filesUrls && patientData.filesUrls.length > 0;
  }

  onDocumentsButtonClick(patientData: any, event: MouseEvent): void {
    // Prevent from navigating to the patient details page
    event.preventDefault();
    event.stopPropagation();

    const modalRef = this.modalService.open(PreviewDocumentsModalComponent);
    modalRef.componentInstance.filesUrls = patientData.filesUrls;
  }


  addExportRequest() {
      let obj = {
        patientPhoneOrPspCode:this.searchParams.searchTerm?.length > 0 ? this.searchParams.searchTerm : null,
        companyId: this.searchParams.companyId,
        sheetType: 1,
        isHomeDelivery: false,
        pageSize: this.totalItems
      };

      this.medicalRequestService.addExportRequest(obj).subscribe(
        (response) => {
          if (response.data) {
            showNotification(
              'success',
              response.message,
              this.toastr
            );
          }
        }
      );
  }

}
